<template>
    <p-modal
        size="lg"
        :label="pageTitle"
        ref="authorList"
        name="exportAuthorListModal"
        @ok.prevent="handleModalOk"
        @shown="loadData"
        :disable-button="disabled"
        @show="initialize"
    >
        <template v-slot:default>
            <p-form ref="form">
                <div>
                    <b-row cols="1" cols-md="2">
                        <b-col>
                            <p-tree
                                :data="filteredData"
                                include-search-filter
                                :filter-place-holder="'Search by campus'"
                                :key="filteredData.length"
                                @selection-change="onTreeSelection"
                            >
                            </p-tree>
                            <p-advanced-select
                                class="mt-3"
                                label="Sort Order"
                                :options="sortOrderOptions"
                                text-field="text"
                                value-field="value"
                                v-model="authorList.sortOrder"
                            />
                        </b-col>
                        <b-col>
                            <p-card class="mb-2 px-2 py-2" no-body>
                                <p-filter-select
                                    data-type="courseRequestBookStatuses"
                                    label="Request Type"
                                    :initial-display-count="8"
                                    :default-selected="true"
                                    v-model="
                                        authorList.CourseRequirementStatusIds
                                    "
                                />
                            </p-card>
                        </b-col>
                    </b-row>
                    <UserReportTemplate 
                        :user-report-template-options="userReportTemplateOptions"
                        v-model:selected-user-report-template-id="selectedUserReportTemplateId"
                        :selected-user-report-template="selectedUserReportTemplate"
                        v-model:template-name="templateName"
                        @selected-object="applyUserReportTemplate($event)" />
                </div>
            </p-form>
        </template>
        <template v-slot:modal-footer="{ ok, cancel }">
            <b-row>
                <b-col>
                    <p-button variant="outline-primary" 
                        :is-busy="isBusy" @click="cancel"
                        >Cancel</p-button
                    >
                    <p-button
                        variant="primary"
                        @click="ok"
                        :is-busy="isBusy"
                        class="ms-2"
                        >OK</p-button
                    >
                </b-col>
            </b-row>
        </template>
    </p-modal>
</template>
<script>
import axios from 'axios';
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';
import StoreListManager from '@/components/mixins/StoreListManager.js';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import ReportTemplates from '@/components/mixins/ReportTemplates.js';
import UserReportTemplate from '@/components/UserReportTemplate.vue';

export default {
    mixins: [StoreListManager, ReportTemplates],
    components: { UserReportTemplate },
    props: {
        disabled: {
            type: Boolean,
            default: false
        },
        termId: {
            type: [Number],
            default: 0
        },
        campusId: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            isBusy: false,
            // Identifies which report saved templates belong to; must match the report name in Reports.vue.
            reportName: 'Author List',
            reportType: 'pdf',
            authorList: {
                CourseRequirementStatusIds: [],
                sortOrder: 'AuthorTitleOrDescription',
                termSections: []
            },
            sortOrderOptions: [
                { text: 'Department Name', value: 'Department Name' },
                {
                    text: 'Department Description',
                    value: 'Department Description'
                },
                { text: 'Course', value: 'Course' },
                { text: 'Section', value: 'Section' },
                { text: 'Requested Quantity', value: 'Requested Quantity' },
                { text: 'Author/Title', value: 'AuthorTitleOrDescription' }
            ],
            terms: [],
            campuses: [],
            treeData: [],
            treeSelection: [],
            expandAll: false
        };
    },
    watch: {
        $route: function(to, from) {
            if (to !== from) {
                this.loadData();
            }
        },
        expandAll() {
            this.setTreeData();
        }
    },
    computed: {
        pageTitle() {
            return 'Author List (PDF)';
        },

        filteredData() {
            return this.treeData;
        },
        // ReportTemplates works against `reportParameters`; this report's parameters object is
        // `authorList`. Same object, so the mixin's Object.assign still mutates it in place.
        reportParameters() {
            return this.authorList;
        }
    },
    methods: {
        initialize(){           
            this.treeSelection = [];
            this.setTreeData();
        },
        async show(reportType = 'pdf') {
            this.reportType = reportType;
            this.$refs.authorList.show();
        },
        // ReportTemplates hook: return false when anything in the saved template can no longer
        // be selected, and the mixin warns and loads nothing. Every saved value here points at
        // data that can change between saving and loading, so each one gets checked against the
        // same list that populates its control.
        async validateReportParameters(savedParameters) {
            const [terms, campuses, requestTypes] = await Promise.all([
                selectListOptionsDataContext.getStoreDropdownData('terms'),
                selectListOptionsDataContext.getStoreDropdownData('campuses'),
                selectListOptionsDataContext.getStoreDropdownData('courseRequestBookStatuses')
            ]);

            // Scalar against a fixed local list: a saved template can predate a change to it.
            if (savedParameters.sortOrder && !this.sortOrderOptions.some(sortOrderOption => sortOrderOption.value === savedParameters.sortOrder)) {
                return false;
            }

            // Id array against a select list: every saved request type must still exist.
            const hasValidRequestTypes = (savedParameters.CourseRequirementStatusIds ?? []).every(
                courseRequirementStatusId =>requestTypes.some(requestType => requestType.value == courseRequirementStatusId));

            if (!hasValidRequestTypes) {
                return false;
            }

            // Tree selections: each saved term must still exist, its campus must still exist, and
            // the term must still belong to that campus — a term moved to another campus would no
            // longer appear under the saved branch of the tree.
            return (savedParameters.termSections ?? []).every(termSection => {
                const term = terms.find(x => x.value == termSection.termId);

                return term
                    && term.campusId == termSection.campusId
                    && campuses.some(campus => campus.value == termSection.campusId);
            });
        },
        // ReportTemplates hook: p-tree keeps the selection as `selected` flags on the nodes
        // themselves, and termSections is only a projection of those flags. Restoring the
        // projection alone would leave the tree unchecked, and the next tree click would
        // recompute termSections from the flags and discard what was restored.
        onUserReportTemplateApplied() {
            const savedTermSections = this.authorList.termSections ?? [];

            this.treeData.forEach(campus => {
                campus.children.forEach(term => {
                    term.selected = savedTermSections.some(termSection => termSection.termId == term.value);
                });

                // A campus is only checked when all of its terms are; otherwise p-tree renders
                // the indeterminate dash from the child flags.
                campus.selected = campus.children.length > 0 && campus.children.every(term => term.selected);

                // Open the branches holding a restored selection so it is visible.
                if (campus.children.some(term => term.selected)) {
                    campus.opened = true;
                }
            });

            // Re-derive from the flags so termSections and the tree agree — this also drops any
            // saved term that is no longer in the tree.
            this.onTreeSelection(this.treeData);
        },
        handleModalOk: async function() {
           if (this.authorList.termSections.length === 0) {
                this.$appError('Please select one or more terms.');
                return;
            }
            this.isBusy = true;

            if (!(await this.saveUserReportTemplate())) {
                this.isBusy = false;
                return;
            }            

            await axios
                .post('terms/exportAuthorList', this.authorList, {
                    responseType: 'blob'
                })
                .then(result => {
                    downloadFileToBrowser(
                        result.data,
                        'Author List',
                        'application/pdf'
                    );
                    this.$appSuccess(`Author list generated successfully.`)
                    this.$refs.authorList.hide();
                })
                .finally(() => {
                    this.isBusy = false;
                });
        },
        loadData() {},
        setTreeData() {
            this.treeData = this.campuses.map(t => ({
                ...t,
                icon: 'fa fa-university',
                level: 'campus',
                opened: this.expandAll,
                children: this.terms
                    .filter(x => x.campusId === t.value)
                    .map(x => ({
                        value: x.value,
                        text: x.text,
                        campusId: t.value,
                        campusName: t.text,
                        icon: 'fa fa-thumbtack',
                        level: 'term'
                    }))
            }));

            this.onTreeSelection(this.filteredData);
        },
        onTreeSelection(data) {
            this.treeSelection = data
                .flatMap(x => x.children)
                .filter(x => x.selected)
                .map(x => ({
                    termId: x.value,
                    termName: x.text,
                    campusId: x.campusId,
                    campusName: x.campusName
                }));
            this.authorList.termSections = this.treeSelection;
            this.$emit('selection-change', this.treeSelection);
        }
    },
    mounted: async function() {
        this.terms = await selectListOptionsDataContext.getStoreDropdownData(
            'terms'
        );

        this.campuses = await selectListOptionsDataContext.getStoreDropdownData(
            'campuses'
        );

        this.setTreeData();
    }
};
</script>
